/**
 * Guest layout claim storage.
 *
 * When a guest uploads a floor plan (no Bearer token available at upload time),
 * we record the SavedLayout uuid locally so we can send it to the backend at the
 * next successful auth (signup or login) and have it claimed for the new user.
 *
 * Storage key: mf.guestLayoutClaims.v1
 * Storage shape:
 *   {
 *     version: 1,
 *     entries: Array<{
 *       uuid: string,
 *       createdAt: number,   // Date.now() at write time
 *       galleryUrl?: string, // optional, for UI copy
 *       source?: string,     // optional, for analytics ("embedded"/"fullscreen"/etc.)
 *     }>,
 *   }
 *
 * All functions are defensive: they swallow storage errors, JSON parse errors,
 * missing key, and wrong-shape data. They never throw.
 */

const STORAGE_KEY = "mf.guestLayoutClaims.v1";
const SESSION_CLAIM_PREFIX = "mf.guestLayoutClaim.session.";
const CURRENT_VERSION = 1;
const DEFAULT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days — matches backend recency guard
const MAX_ENTRIES = 20; // Backend accepts up to 20; we FIFO-evict beyond this.

function safeGetStorage() {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage || null;
  } catch (_err) {
    return null;
  }
}

function readState() {
  const storage = safeGetStorage();
  if (!storage) return { version: CURRENT_VERSION, entries: [] };
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return { version: CURRENT_VERSION, entries: [] };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || !Array.isArray(parsed.entries)) {
      return { version: CURRENT_VERSION, entries: [] };
    }
    const entries = parsed.entries.filter(
      (e) => e && typeof e.uuid === "string" && typeof e.createdAt === "number"
    );
    return { version: CURRENT_VERSION, entries };
  } catch (err) {
    console.warn("[guestLayoutStorage] Failed to read state:", err);
    return { version: CURRENT_VERSION, entries: [] };
  }
}

function writeState(state) {
  const storage = safeGetStorage();
  if (!storage) return;
  try {
    storage.setItem(
      STORAGE_KEY,
      JSON.stringify({ version: CURRENT_VERSION, entries: state.entries || [] })
    );
  } catch (err) {
    console.warn("[guestLayoutStorage] Failed to write state:", err);
  }
}

function removeKey() {
  const storage = safeGetStorage();
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_KEY);
  } catch (err) {
    console.warn("[guestLayoutStorage] Failed to remove key:", err);
  }
}

function markSessionGuestLayout(uuid) {
  try {
    if (typeof window === "undefined" || !window.sessionStorage || !uuid) return;
    window.sessionStorage.setItem(`${SESSION_CLAIM_PREFIX}${uuid}`, "1");
  } catch (_err) {
    /* ignore */
  }
}

function hasSessionGuestLayout(uuid) {
  try {
    if (typeof window === "undefined" || !window.sessionStorage || !uuid) return false;
    return window.sessionStorage.getItem(`${SESSION_CLAIM_PREFIX}${uuid}`) === "1";
  } catch (_err) {
    return false;
  }
}

/**
 * Append a guest upload uuid with metadata. No-op without a uuid. FIFO-caps at 20.
 * @param {{ uuid: string, galleryUrl?: string, source?: string }} entry
 */
export function addGuestLayout(entry) {
  try {
    if (!entry || typeof entry.uuid !== "string" || !entry.uuid) return;
    markSessionGuestLayout(entry.uuid);
    const state = readState();
    // De-dup by uuid (last write wins).
    const withoutDup = state.entries.filter((e) => e.uuid !== entry.uuid);
    const next = [
      ...withoutDup,
      {
        uuid: entry.uuid,
        createdAt: Date.now(),
        ...(entry.galleryUrl ? { galleryUrl: entry.galleryUrl } : {}),
        ...(entry.source ? { source: entry.source } : {}),
      },
    ];
    // FIFO evict oldest beyond MAX_ENTRIES.
    const capped = next.slice(-MAX_ENTRIES);
    writeState({ entries: capped });
  } catch (err) {
    console.warn("[guestLayoutStorage] addGuestLayout failed:", err);
  }
}

/**
 * Returns up to `limit` non-expired uuids, pruning expired entries back to storage.
 * @param {{ maxAgeMs?: number, limit?: number }} [opts]
 * @returns {string[]}
 */
export function getGuestLayoutUuids(opts = {}) {
  try {
    const maxAgeMs =
      typeof opts.maxAgeMs === "number" ? opts.maxAgeMs : DEFAULT_MAX_AGE_MS;
    const limit = typeof opts.limit === "number" ? opts.limit : MAX_ENTRIES;
    const state = readState();
    const now = Date.now();
    const fresh = state.entries.filter((e) => now - e.createdAt <= maxAgeMs);
    if (fresh.length !== state.entries.length) {
      // Persist pruned state so we don't carry expired entries forever.
      writeState({ entries: fresh });
    }
    return fresh.slice(0, limit).map((e) => e.uuid);
  } catch (err) {
    console.warn("[guestLayoutStorage] getGuestLayoutUuids failed:", err);
    return [];
  }
}

/**
 * Remove specific uuids, or clear the key entirely when called with no arg / empty array.
 * @param {string[]} [uuidsToClear]
 */
export function clearGuestLayouts(uuidsToClear) {
  try {
    if (!Array.isArray(uuidsToClear) || uuidsToClear.length === 0) {
      removeKey();
      return;
    }
    const toRemove = new Set(uuidsToClear);
    const state = readState();
    const remaining = state.entries.filter((e) => !toRemove.has(e.uuid));
    if (remaining.length === 0) {
      removeKey();
      return;
    }
    writeState({ entries: remaining });
  } catch (err) {
    console.warn("[guestLayoutStorage] clearGuestLayouts failed:", err);
  }
}

/**
 * Boot-time cleanup: drop expired entries.
 * @param {{ maxAgeMs?: number }} [opts]
 */
export function pruneExpired(opts = {}) {
  try {
    const maxAgeMs =
      typeof opts.maxAgeMs === "number" ? opts.maxAgeMs : DEFAULT_MAX_AGE_MS;
    const state = readState();
    const now = Date.now();
    const fresh = state.entries.filter((e) => now - e.createdAt <= maxAgeMs);
    if (fresh.length !== state.entries.length) {
      writeState({ entries: fresh });
    }
  } catch (err) {
    console.warn("[guestLayoutStorage] pruneExpired failed:", err);
  }
}

/**
 * True when this browser session uploaded the layout as a guest (pending claim).
 * @param {{ uuid?: string } | null | undefined} layout
 * @returns {boolean}
 */
export function isGuestClaimedLayout(layout) {
  try {
    const uuid = layout?.uuid;
    if (!uuid || typeof uuid !== "string") return false;
    return getGuestLayoutUuids().includes(uuid) || hasSessionGuestLayout(uuid);
  } catch (_err) {
    return false;
  }
}
